import { useCallback } from 'react'
import { useArcVoiceCall } from '../../contexts/ArcVoiceCallContext'
import { ArcVoiceCallStatus, IArcVoiceCallSnapshot } from '../../helpers/arcVoice/arcVoiceCall'
import { readyToTell } from '../../helpers/arcVoice/callAgenda'
import {
  callStatusLabel,
  isRunningOffStage,
  orbStateFor,
} from '../../helpers/arcVoice/callPresentation'
import useCallClock from '../../hooks/useCallClock'
import useOpenConversation from '../../hooks/useOpenConversation'
import Icon from '../Common/Icon'
import IconButton from '../Common/IconButton'
import Text from '../Common/Text'
import styles from './ArcVoice.module.scss'
import ArcVoiceOrb from './ArcVoiceOrb'

interface IProps {
  /** The AIM panel exists here (desktop), so the call can come back without leaving the page. */
  canUseBubble: boolean
}

interface ICallClockProps {
  snapshot: IArcVoiceCallSnapshot
}

/** Its own component, so the tick re-renders the clock and not the pill. */
const CallClock = ({ snapshot }: ICallClockProps) => {
  const clock = useCallClock(snapshot)
  return <span className={styles.pillClock}>{clock}</span>
}

/**
 * A call that is running somewhere you aren't looking: a small orb, its clock,
 * and the way back to it. Steps aside while a call stage is on screen, and for a
 * call brought into a huddle.
 */
const ArcVoiceCallPill = ({ canUseBubble }: IProps) => {
  const { call, snapshot, isStageVisible, setIsMinimized, roomHuddleId } = useArcVoiceCall()
  const openConversation = useOpenConversation(canUseBubble)

  const getLevels = useCallback(() => call?.levels ?? { input: 0, output: 0 }, [call])
  const channelId = snapshot?.channelId
  const handleReturn = useCallback(() => {
    if (!channelId) return
    setIsMinimized(false)
    openConversation(channelId)
  }, [channelId, openConversation, setIsMinimized])
  const handleTogglePause = useCallback((): void => {
    if (snapshot?.status === ArcVoiceCallStatus.Paused) void call?.resume()
    else call?.pause()
  }, [call, snapshot?.status])
  const handleToggleMute = useCallback((): void => {
    call?.toggleMute()
  }, [call])
  const handleStopWork = useCallback((): void => {
    void call?.stopWork()
  }, [call])
  const handleEnd = useCallback((): void => {
    void call?.end()
  }, [call])

  // In a huddle, Arc shows in the huddle's pill instead.
  if (!call || roomHuddleId || !isRunningOffStage(snapshot, isStageVisible)) return null
  const isPaused = snapshot.status === ArcVoiceCallStatus.Paused
  const readyCount = readyToTell(snapshot.agenda).length
  const { isMuted } = snapshot

  return (
    <div className={styles.pill} role="region" aria-label="Arc Voice">
      <ArcVoiceOrb state={orbStateFor(snapshot)} getLevels={getLevels} size="sm" />
      <div className={styles.pillText}>
        <Text variant="title" component="span" className={styles.pillTitle}>
          Arc Voice
        </Text>
        <Text
          variant="bodySm"
          tone="secondary"
          casing="role"
          component="span"
          className={styles.pillStatus}
          role="status"
        >
          {callStatusLabel(snapshot)}
        </Text>
      </div>
      {readyCount > 0 && (
        <Text
          variant="pill"
          tone="info"
          className={styles.pillReady}
          title="Results Arc will tell you at the next pause"
        >
          {readyCount} ready
        </Text>
      )}
      <CallClock snapshot={snapshot} />
      <IconButton title="Back to Arc Voice" box="sm" onClick={handleReturn}>
        <Icon name="window-open" size="sm" />
      </IconButton>
      {!isPaused && (
        <IconButton
          title={isMuted ? 'Unmute your mic' : 'Mute your mic'}
          box="sm"
          className={isMuted ? styles.pillMuted : undefined}
          onClick={handleToggleMute}
          aria-pressed={isMuted}
        >
          <Icon name={isMuted ? 'mic-off' : 'mic'} size="sm" />
        </IconButton>
      )}
      {snapshot.isArcWorking && (
        <IconButton title="Stop what Arc is working on" box="sm" onClick={handleStopWork}>
          <Icon name="stop" size="sm" />
        </IconButton>
      )}
      <IconButton title={isPaused ? 'Resume' : 'Pause'} box="sm" onClick={handleTogglePause}>
        <Icon name={isPaused ? 'play' : 'pause-circle'} size="sm" />
      </IconButton>
      <IconButton title="Hang up" box="sm" className={styles.pillEnd} onClick={handleEnd}>
        <Icon name="phone" size="sm" />
      </IconButton>
    </div>
  )
}

export default ArcVoiceCallPill
